/**
 * DiscoverPage — Infinite scroll AI-powered personalized feed
 * Route: /discover
 * Tabs: For You | Nearby | Global Inspiration
 * Gemini generates batches, mixed with trending user FoodCards
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Sparkles, MapPin, Globe, Navigation, Layers, Camera, Share2, Star,
  ChevronDown, Loader, ThumbsDown, Bookmark, ArrowLeft, Zap
} from 'lucide-react';
import { useLocation } from '../contexts/LocationContext';
import { useAuth } from '../contexts/AuthContext';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { key: 'for_you', label: 'For You', icon: Sparkles },
  { key: 'nearby', label: 'Nearby', icon: Navigation },
  { key: 'global', label: 'Global', icon: Globe },
];

const TYPE_BADGES = {
  hidden_gem: { label: 'Hidden Gem', bg: 'bg-emerald-500' },
  quest_idea: { label: 'Quest', bg: 'bg-purple-500' },
  trending_dish: { label: 'Trending', bg: 'bg-[#FF6B35]' },
  nearby_spot: { label: 'Nearby', bg: 'bg-blue-500' },
  user_foodcard: { label: 'FoodCard', bg: 'bg-gray-700' },
};

const CTA_STYLES = {
  'Explore': 'bg-gray-900 text-white',
  'Start Quest': 'bg-purple-600 text-white',
  'Verify & Repost': 'bg-emerald-600 text-white',
};

const DiscoverPage = () => {
  const { locationString, location: userLoc } = useLocation();
  const { user } = useAuth();
  const [tab, setTab] = useState('for_you');
  // Init from prefetch cache for instant render
  const [items, setItems] = useState(() => {
    try {
      const cacheKey = `swr_cache:${API_URL}/api/discover-feed?tab=for_you&page=1&limit=10`;
      const raw = localStorage.getItem(cacheKey);
      if (raw) {
        const { data, ts } = JSON.parse(raw);
        if (Date.now() - ts < 30 * 60 * 1000 && data?.items) return data.items;
      }
    } catch {}
    return [];
  });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(() => {
    try {
      const cacheKey = `swr_cache:${API_URL}/api/discover-feed?tab=for_you&page=1&limit=10`;
      return !localStorage.getItem(cacheKey);
    } catch { return true; }
  });
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [excludeTitles, setExcludeTitles] = useState([]);
  const observerRef = useRef(null);
  const sentinelRef = useRef(null);

  const token = localStorage.getItem('fq_auth_token');
  const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

  const fetchBatch = useCallback(async (pageNum, append = false) => {
    if (append) setLoadingMore(true); else setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pageNum.toString(),
        limit: '10',
        location: locationString || '',
        tab,
        exclude: excludeTitles.join(','),
      });
      const res = await fetch(`${API_URL}/api/discover-feed?${params}`, { credentials: 'include', headers });
      if (res.ok) {
        const data = await res.json();
        const newItems = data.items || [];
        if (append) {
          setItems(prev => [...prev, ...newItems]);
        } else {
          setItems(newItems);
        }
        setHasMore(data.has_more && newItems.length > 0);
        setExcludeTitles(prev => [...prev, ...newItems.map(i => i.title)]);
      }
    } catch {} finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [locationString, tab, excludeTitles]);

  // Initial load + tab change
  useEffect(() => {
    setPage(1);
    setItems([]);
    setExcludeTitles([]);
    setHasMore(true);
    fetchBatch(1, false);
  }, [tab, locationString]);

  // Infinite scroll observer
  useEffect(() => {
    if (!sentinelRef.current) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && hasMore && !loading && !loadingMore) {
        const next = page + 1;
        setPage(next);
        fetchBatch(next, true);
      }
    }, { threshold: 0.1 });
    obs.observe(sentinelRef.current);
    observerRef.current = obs;
    return () => obs.disconnect();
  }, [hasMore, loading, loadingMore, page]);

  const handleFeedback = async (title, action) => {
    try {
      await fetch(`${API_URL}/api/discover-feed/feedback`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...headers }, credentials: 'include',
        body: JSON.stringify({ title, action }),
      });
    } catch {}
    if (action === 'not_interested') {
      setItems(prev => prev.filter(i => i.title !== title));
    }
  };

  const handleCTA = (item) => {
    if (item.action_cta === 'Start Quest' || item.action_cta === 'Verify & Repost') {
      window.dispatchEvent(new CustomEvent('open-foodcard-modal', {
        detail: { mode: 'quest_verify', questContext: { quest_id: `discover_${Date.now()}`, quest_title: item.title, quest_blurb: item.description } }
      }));
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] pb-20 md:pb-8">
      {/* Hero */}
      <div className="relative" style={{ minHeight: '180px' }}>
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-[#FF6B35]/30" />
        <div className="relative flex flex-col justify-end max-w-4xl mx-auto px-4" style={{ minHeight: '180px' }}>
          <div className="mb-5">
            <Link to="/" className="inline-flex items-center gap-1 text-white/60 hover:text-white text-xs mb-2">
              <ArrowLeft className="w-3 h-3" /> Home
            </Link>
            <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-2">
              <Sparkles className="w-7 h-7 text-[#F4C430]" /> Discover
            </h1>
            <div className="flex items-center gap-2 mt-1.5">
              {locationString ? (
                <span className="text-white/60 text-sm flex items-center gap-1">
                  <MapPin className="w-3 h-3" /> {locationString}
                  {tab !== 'global' && <span className="text-[#F4C430] text-xs ml-1">• Local</span>}
                </span>
              ) : (
                <span className="text-white/50 text-sm">Set location for personalized picks</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="sticky top-0 z-30 bg-white/95 backdrop-blur-lg border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 flex gap-1 py-2">
          {TABS.map(t => {
            const Icon = t.icon;
            return (
              <button key={t.key} onClick={() => setTab(t.key)}
                className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-full transition-all ${
                  tab === t.key ? 'bg-gray-900 text-white' : 'text-gray-500 hover:bg-gray-100'
                }`} data-testid={`discover-tab-${t.key}`}>
                <Icon className="w-4 h-4" />{t.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Feed */}
      <div className="max-w-4xl mx-auto px-4 py-5">
        {loading ? (
          <div className="space-y-4">
            {[1,2,3,4].map(i => (
              <div key={i} className="bg-white rounded-[20px] p-5 animate-pulse">
                <div className="h-4 bg-gray-100 rounded w-1/3 mb-3" />
                <div className="h-3 bg-gray-100 rounded w-full mb-2" />
                <div className="h-3 bg-gray-100 rounded w-2/3 mb-4" />
                <div className="h-10 bg-gray-100 rounded-2xl w-32" />
              </div>
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-16">
            <Sparkles className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-bold text-gray-700">No discoveries yet</h3>
            <p className="text-sm text-gray-500 mt-1">Set your location to get personalized picks</p>
          </div>
        ) : (
          <div className="space-y-4">
            {items.map((item, i) => {
              const badge = TYPE_BADGES[item.type] || TYPE_BADGES.hidden_gem;
              const ctaStyle = CTA_STYLES[item.action_cta] || CTA_STYLES.Explore;
              const isUserCard = item.type === 'user_foodcard';
              const isAI = item.image_source === 'ai_generated';

              return (
                <div key={`${item.title}-${i}`} className="bg-white rounded-[20px] overflow-hidden shadow-[0_1px_3px_rgba(0,0,0,0.06)] hover:shadow-md transition-all" data-testid={`discover-item-${i}`}>
                  {/* User FoodCard with image */}
                  {isUserCard && item.image_url && (
                    <div className="relative h-48 overflow-hidden">
                      <img src={item.image_url} alt={item.title} className="w-full h-full object-cover" />
                      <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/60 to-transparent h-20" />
                      <div className="absolute bottom-3 left-3 flex items-center gap-2">
                        <Avatar className="w-6 h-6 ring-2 ring-white">
                          <AvatarImage src={item.user_avatar} />
                          <AvatarFallback className="bg-[#FF6B35] text-white text-[8px]">{(item.user_name || 'F')[0]}</AvatarFallback>
                        </Avatar>
                        <span className="text-white text-xs font-medium">{item.user_name}</span>
                      </div>
                      {item.verified && <span className="absolute top-3 right-3 bg-emerald-500 text-white text-[9px] font-bold px-2 py-0.5 rounded-full">Verified</span>}
                    </div>
                  )}

                  <div className="p-5">
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1.5">
                          <span className={`${badge.bg} text-white text-[9px] font-bold px-2 py-0.5 rounded-full`}>{badge.label}</span>
                          {isAI && <span className="bg-gray-200 text-gray-600 text-[8px] font-bold px-1.5 py-0.5 rounded-full flex items-center gap-0.5"><Sparkles className="w-2.5 h-2.5" />AI</span>}
                          {!isAI && isUserCard && <span className="bg-blue-50 text-blue-600 text-[8px] font-bold px-1.5 py-0.5 rounded-full">Real</span>}
                          {item.cuisine && <span className="text-[10px] text-gray-400 font-medium">{item.cuisine}</span>}
                          {item.vibe && <span className="text-[10px] text-gray-400">• {item.vibe}</span>}
                        </div>
                        <h3 className="text-base font-bold text-gray-900 leading-snug">{item.title}</h3>
                      </div>
                    </div>

                    <p className="text-sm text-gray-600 leading-relaxed mb-2">{item.description}</p>

                    {/* AI disclosure */}
                    {isAI && item.ai_disclosure && (
                      <p className="text-[10px] text-gray-400 italic mb-2 flex items-center gap-1">
                        <Sparkles className="w-3 h-3 flex-shrink-0" />{item.ai_disclosure}
                      </p>
                    )}

                    {item.why_for_user && (
                      <p className="text-xs text-[#FF6B35] font-medium mb-2 flex items-center gap-1">
                        <Zap className="w-3 h-3" />{item.why_for_user}
                      </p>
                    )}

                    {item.location_hint && (
                      <p className="text-xs text-gray-400 mb-3 flex items-center gap-1">
                        <MapPin className="w-3 h-3" />{item.location_hint}
                      </p>
                    )}

                    {/* Diet tags */}
                    {item.diet_tags?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-3">
                        {item.diet_tags.map(t => <span key={t} className="bg-emerald-50 text-emerald-700 text-[9px] font-medium px-2 py-0.5 rounded-full">{t}</span>)}
                      </div>
                    )}

                    {/* Actions row */}
                    <div className="flex items-center justify-between">
                      <button onClick={() => handleCTA(item)}
                        className={`px-5 py-2.5 text-xs font-bold rounded-2xl active:scale-95 transition-all ${ctaStyle}`}>
                        {item.action_cta || 'Explore'}
                      </button>
                      <div className="flex items-center gap-1">
                        <button onClick={() => handleFeedback(item.title, 'save')}
                          className="p-2 text-gray-400 hover:text-[#FF6B35] hover:bg-[#FFF5F0] rounded-full transition-colors" title="Save">
                          <Bookmark className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleFeedback(item.title, 'not_interested')}
                          className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors" title="Not interested">
                          <ThumbsDown className="w-4 h-4" />
                        </button>
                        <button className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-full transition-colors" title="Share">
                          <Share2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Infinite scroll sentinel */}
            <div ref={sentinelRef} className="h-4" />

            {loadingMore && (
              <div className="flex items-center justify-center py-6 gap-2 text-gray-500">
                <Loader className="w-5 h-5 animate-spin text-[#FF6B35]" />
                <span className="text-sm">Loading more discoveries...</span>
              </div>
            )}

            {!hasMore && items.length > 0 && (
              <div className="text-center py-8">
                <p className="text-sm text-gray-400">You've seen all current discoveries!</p>
                <button onClick={() => { setPage(1); setItems([]); setExcludeTitles([]); setHasMore(true); fetchBatch(1); }}
                  className="mt-2 text-sm text-[#FF6B35] font-semibold hover:underline">Refresh for new picks</button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DiscoverPage;
